import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Search, CalculatorIcon, FileText, Barcode, Trash2, RotateCcw, Wallet, Truck, Table2, X as XIcon, ArrowDownToLine, ArrowUpFromLine, RefreshCw, Lock, Percent } from 'lucide-react';

/**
 * Tile de comando padronizado — todos com o MESMO tamanho (h-16), quadrado/organizado,
 * estilo "tecla de PDV": ícone em cima, rótulo, e o atalho de teclado.
 */
const CommandTile = ({ onClick, disabled, icon: Icon, label, shortcut, color = 'blue', testid, title, variant = 'compact' }) => {
  const palette = {
    blue: 'bg-blue-800 hover:bg-blue-900 border-blue-900',
    red: 'bg-red-600 hover:bg-red-700 border-red-800',
    amber: 'bg-amber-600 hover:bg-amber-700 border-amber-800',
    emerald: 'bg-emerald-600 hover:bg-emerald-700 border-emerald-800',
  };
  if (variant === 'square') {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        data-testid={testid}
        title={title}
        className={`aspect-square min-h-[60px] max-h-[140px] flex flex-col items-center justify-center gap-1 rounded-md border text-white transition-colors active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed ${palette[color]}`}
      >
        <Icon size={22} />
        <span className="text-xs font-bold leading-none text-center px-1 truncate max-w-full">{label}</span>
        {shortcut && (
          <span className="text-[9px] font-mono opacity-90 leading-none bg-black/25 px-1.5 py-0.5 rounded">{shortcut}</span>
        )}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      title={title}
      className={`flex items-center justify-center gap-2 h-11 px-2 rounded-md border text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${palette[color]}`}
    >
      <Icon size={18} className="shrink-0" />
      <span className="text-xs font-bold leading-none truncate">{label}</span>
      {shortcut && (
        <span className="text-[9px] font-mono opacity-90 leading-none bg-black/25 px-1.5 py-0.5 rounded shrink-0">{shortcut}</span>
      )}
    </button>
  );
};

/**
 * Barra lateral direita do PDV (45% da tela). Layout compacto: Total, card NF-e,
 * botão Finalizar em destaque e TODOS os botões de comando padronizados em tiles
 * uniformes (mesmo tamanho), organizados em grade — sem necessidade de rolagem.
 */
export const PDVSidebar = ({
  barcodeInput, setBarcodeInput, handleManualBarcode, processInput,
  pendingWeight, clearPendingWeight,
  calculateTotal, calculateSubtotal, discount,
  activeItems, cancelledItems, setShowDiscountModal,
  selectedCustomer, customerName, customerCpf,
  nfeMode, setNfeMode,
  setShowPaymentModal,
  hasActiveItems, setShowCancelSaleModal,
  openReverseLastSale, reverseLoading, cart,
  setShowSearch, setShowCalculator, setShowReceivablesModal, setShowPreSalesModal,
  setShowDeliveryModal, delivery,
  loadedTableNumber, saveBackToTable, setCart, setLoadedPreSaleId, setLoadedTableNumber,
  user, setShowSwitchOperator, setShowCloseShift, setShowSangriaModal, setShowSuprimentoModal,
}) => {
  const cleanDoc = (customerCpf || '').replace(/\D/g, '');
  const isCnpj = cleanDoc.length === 14;
  const hasClient = !!selectedCustomer || !!customerName;
  // Último produto lançado na venda (último item ativo do carrinho)
  const lastItem = [...(cart || [])].reverse().find((i) => !i.cancelled) || null;

  return (
    <div className="w-full lg:w-[45%] pdv-side-area border-l flex flex-col overflow-visible lg:overflow-y-auto order-1 lg:order-none">
      <div className="p-3 flex flex-col gap-2 lg:min-h-full">
        {/* Código de barras */}
        <div className="shrink-0">
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold pdv-text-muted mb-1 block flex items-center gap-2">
            <Barcode size={12} /> Código de Barras / Produto
            <span className="ml-auto px-1.5 py-0.5 rounded bg-blue-600/15 text-blue-600 text-[9px] font-black tracking-normal" data-testid="barcode-f2-badge">F2</span>
          </Label>
          {pendingWeight != null && (
            <div
              className="mb-1 flex items-center justify-between gap-2 rounded bg-amber-100 border border-amber-300 px-2 py-1"
              data-testid="pending-weight-badge"
            >
              <span className="text-[11px] font-bold text-amber-800">
                {Number.isInteger(Number(pendingWeight))
                  ? `Quantidade armada: ${Number(pendingWeight)}`
                  : `Peso armado: ${Number(pendingWeight).toLocaleString('pt-BR', { minimumFractionDigits: 3 })}`} — escolha o produto
              </span>
              <button
                type="button"
                onClick={clearPendingWeight}
                className="text-amber-700 hover:text-amber-900 text-[11px] font-bold underline"
                data-testid="clear-pending-weight-btn"
              >
                limpar
              </button>
            </div>
          )}
          <div className="flex gap-2">
            <Input
              placeholder='Código ou "qtd*código"'
              value={barcodeInput}
              onChange={(e) => {
                const v = e.target.value;
                // Arma a quantidade/peso assim que o operador digita "N*" (ex.: 5* ou 0,985*),
                // mesmo sem pressionar Enter — assim funciona ao clicar em Busca logo em seguida.
                if (/^\d+[.,]?\d*\*$/.test(v.trim())) {
                  processInput(v.trim());
                  setBarcodeInput('');
                } else {
                  setBarcodeInput(v);
                }
              }}
              onKeyDown={handleManualBarcode}
              className="flex-1 pdv-input h-11 text-base"
              data-testid="barcode-input-side"
              autoFocus
            />
            <Button
              onClick={() => { if (barcodeInput.trim()) { processInput(barcodeInput.trim()); setBarcodeInput(''); }}}
              className="bg-blue-600 hover:bg-blue-700 h-11 px-5"
            >
              Add
            </Button>
          </div>
        </div>

        {/* Valor da Venda — preenche o espaço vago; total gigante e legível de longe */}
        <div className="pdv-total-card rounded-md p-3 flex-1 flex flex-col justify-center min-h-[110px]" data-testid="valor-da-venda-card">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-[0.25em] font-bold opacity-90">Valor da Venda</p>
            <span className="text-xs opacity-90">{activeItems.length} {activeItems.length === 1 ? 'item' : 'itens'}{cancelledItems.length > 0 ? ` · ${cancelledItems.length} canc.` : ''}</span>
          </div>
          <p className="text-5xl sm:text-6xl lg:text-7xl font-black text-right leading-none mt-2 tabular-nums" data-testid="cart-total">
            R$ {calculateTotal().toFixed(2)}
          </p>
          {discount > 0 && (
            <p className="text-sm text-right mt-2 opacity-90">
              Subtotal: R$ {calculateSubtotal().toFixed(2)} · Desconto: <strong>- R$ {discount.toFixed(2)}</strong>
            </p>
          )}
        </div>

        {/* Emitir Nota Fiscal + Finalizar Venda — empilha no celular, lado a lado no tablet+ */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-stretch">
          {/* Quadro Emitir Nota Fiscal */}
          <div
            className={`rounded-md border-2 p-2 flex flex-col ${nfeMode ? 'border-[#008000] bg-[#008000]/15' : (isCnpj ? 'border-[#008000]/70 bg-[#008000]/5' : 'border-slate-300 bg-slate-50')}`}
            data-testid="nfe-info-card"
          >
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1 mb-1"><FileText size={11} /> Emitir Nota Fiscal</p>
            <div className="flex items-start justify-between gap-2 flex-1 min-h-0">
              <div className="min-w-0">
                <p className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">Cliente</p>
                <p className="text-xs font-bold text-slate-800 truncate" data-testid="nfe-customer-label">
                  {hasClient ? (customerName || 'Cliente selecionado') : 'Consumidor Final'}
                </p>
                {customerCpf && (
                  <p className="text-[10px] text-slate-600 font-mono">{isCnpj ? 'CNPJ' : 'CPF'}: {customerCpf}</p>
                )}
              </div>
              <div className="text-right shrink-0">
                {nfeMode ? (
                  <>
                    <p className="text-[9px] uppercase tracking-wider font-black text-[#008000]">ATIVO</p>
                    <p className="text-sm font-black text-[#008000]" data-testid="nfe-suggestion">NF-e 55</p>
                  </>
                ) : isCnpj ? (
                  <>
                    <p className="text-[9px] uppercase tracking-wider font-bold text-[#008000]">Sugerido</p>
                    <p className="text-sm font-black text-[#008000]" data-testid="nfe-suggestion">NF-e 55</p>
                  </>
                ) : (
                  <>
                    <p className="text-[9px] uppercase tracking-wider font-bold text-slate-500">Padrão</p>
                    <p className="text-sm font-black text-slate-700" data-testid="nfe-suggestion">NFC-e 65</p>
                  </>
                )}
              </div>
            </div>
            {nfeMode ? (
              <button
                type="button"
                onClick={() => setNfeMode(false)}
                className="mt-1.5 w-full bg-white hover:bg-red-50 border-2 border-red-400 text-red-700 text-[10px] font-bold uppercase tracking-wider rounded px-2 py-1.5 transition-colors flex items-center justify-center gap-1"
                data-testid="btn-nfe-cancel"
              >
                <XIcon size={12} /> Desativar NF-e
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setNfeMode(true)}
                className="mt-1.5 w-full bg-[#008000] hover:bg-[#006b00] text-white text-[10px] font-bold uppercase tracking-wider rounded px-2 py-1.5 transition-colors flex items-center justify-center gap-1"
                data-testid="btn-nfe-activate"
                title="Marca esta venda para emissão de NF-e (Modelo 55)."
              >
                <FileText size={12} /> Emitir NF-e
              </button>
            )}
          </div>

          {/* Quadro Finalizar Venda — dividido no meio: Último produto (metade) + ação Finalizar (metade) */}
          <div className="rounded-md border-2 border-[#008000] bg-[#008000]/10 p-2 flex flex-col gap-2" data-testid="finalizar-venda-card">
            {/* Metade 1 — descrição do último produto lançado */}
            <div className="flex-1 min-h-0 flex flex-col justify-center border-b border-[#008000]/30 pb-2" data-testid="last-item-added">
              <p className="text-[10px] uppercase tracking-wider font-bold pdv-text-muted mb-1">Último produto lançado</p>
              {lastItem ? (
                <div>
                  <p className="text-base font-bold pdv-text leading-tight truncate" data-testid="last-item-name" title={lastItem.product_name}>{lastItem.product_name}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs pdv-text-secondary font-mono">
                      {Number(lastItem.quantity)} x R$ {Number(lastItem.unit_price).toFixed(2)}
                    </span>
                    <span className="text-base font-black text-[#008000] font-mono" data-testid="last-item-subtotal">R$ {Number(lastItem.subtotal).toFixed(2)}</span>
                  </div>
                </div>
              ) : (
                <p className="text-xs pdv-text-muted italic">Nenhum item lançado</p>
              )}
            </div>
            {/* Metade 2 — ação de finalizar */}
            <Button
              onClick={() => activeItems.length > 0 && setShowPaymentModal(true)}
              disabled={activeItems.length === 0}
              className="w-full flex-1 min-h-[52px] bg-[#008000] hover:bg-[#006b00] text-base font-black tracking-wide leading-tight active:scale-95"
              data-testid="btn-checkout"
            >
              FINALIZAR VENDA <kbd className="ml-1 px-1.5 py-0.5 text-[10px] bg-[#005500] rounded hidden sm:inline-block">F10</kbd>
            </Button>
          </div>
        </div>

        {/* Botões de comando — quadrados iguais, 4 colunas × 2 linhas (ordem exata) */}
        <div className="grid grid-cols-4 gap-2" data-testid="pdv-command-grid">
          <CommandTile variant="square" testid="btn-search-f4" icon={Search} label="Busca" shortcut="F4" color="blue" onClick={() => setShowSearch(true)} />
          <CommandTile variant="square" testid="btn-calc-f5" icon={CalculatorIcon} label="Calc" shortcut="F5" color="blue" onClick={() => setShowCalculator(true)} />
          <CommandTile variant="square" testid="btn-receivables-f7" icon={Wallet} label="Receber" shortcut="F7" color="blue" onClick={() => setShowReceivablesModal(true)} />
          <CommandTile variant="square" testid="btn-pre-sales" icon={Table2} label="Mesa" shortcut="F11" color="blue" onClick={() => setShowPreSalesModal(true)} />
          <CommandTile variant="square" testid="btn-cancel-sale" icon={Trash2} label="Cancelar" shortcut="C" color="red" disabled={!hasActiveItems} onClick={() => hasActiveItems && setShowCancelSaleModal(true)} title="Cancelar a venda inteira (tecla C) — exige senha do operador" />
          <CommandTile variant="square" testid="btn-reverse-last-sale" icon={RotateCcw} label={reverseLoading ? '...' : 'Estorno'} shortcut="F9" color="amber" disabled={reverseLoading || cart.length > 0} onClick={openReverseLastSale} title={cart.length > 0 ? 'Esvazie o carrinho para estornar a última venda' : 'Estornar a última venda do operador (F9 / E)'} />
          <CommandTile variant="square" testid="btn-discount-f8" icon={Percent} label={discount > 0 ? `-${discount.toFixed(2)}` : 'Desconto'} shortcut="F8" color="blue" disabled={!hasActiveItems} onClick={() => activeItems.length > 0 && setShowDiscountModal(true)} title="Aplicar desconto na venda (F8)" />
          <CommandTile variant="square" testid="btn-delivery" icon={Truck} label={delivery ? 'Delivery ✓' : 'Delivery'} color="blue" onClick={() => setShowDeliveryModal(true)} title="Cadastrar/buscar entrega" />
        </div>

        {loadedTableNumber && (
          <div className="bg-yellow-500/20 border-2 border-yellow-500 rounded-md p-2 text-center" data-testid="editing-table-banner">
            <p className="text-[10px] uppercase tracking-wider font-bold text-yellow-300">Editando Mesa {loadedTableNumber}</p>
            <div className="grid grid-cols-2 gap-1 mt-1">
              <button
                type="button"
                onClick={saveBackToTable}
                className="bg-yellow-500 hover:bg-yellow-600 text-slate-900 text-[11px] font-bold uppercase rounded px-2 py-1.5"
                data-testid="btn-save-to-table"
                title="Salvar itens atuais na mesa (mantém aberta)"
              >
                💾 Atualizar Mesa
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!window.confirm('Sair sem salvar? Os itens adicionados/removidos serão descartados.')) return;
                  setCart([]);
                  setLoadedPreSaleId(null);
                  setLoadedTableNumber(null);
                }}
                className="bg-slate-600 hover:bg-slate-700 text-white text-[11px] font-bold uppercase rounded px-2 py-1.5"
                data-testid="btn-cancel-table-edit"
              >
                ✕ Cancelar Edição
              </button>
            </div>
          </div>
        )}

        {/* Operador — nome + botões de comando no MESMO padrão de tile */}
        {user && (
          <div data-testid="pdv-operator-info">
            <div className="flex items-center justify-between px-1 mb-1">
              <div className="min-w-0">
                <p className="text-[9px] uppercase tracking-wider pdv-text-muted font-bold">Caixa Operado por</p>
                <p className="text-sm font-bold pdv-text-primary leading-tight truncate">{user.name}</p>
              </div>
              <span className="text-[10px] pdv-text-muted shrink-0">{(user.role || 'operador').toUpperCase()}</span>
            </div>
            <div className="grid grid-cols-4 gap-2" data-testid="pdv-operator-grid">
              <CommandTile testid="btn-open-sangria" icon={ArrowDownToLine} label="Sangria" color="amber" onClick={() => setShowSangriaModal(true)} title="Retirar dinheiro do caixa (requer senha do operador)" />
              <CommandTile testid="btn-open-suprimento" icon={ArrowUpFromLine} label="Suprimento" color="emerald" onClick={() => setShowSuprimentoModal(true)} title="Entrada de dinheiro no caixa — troco/reforço (requer senha do operador)" />
              <CommandTile testid="btn-switch-operator" icon={RefreshCw} label="Trocar" color="amber" onClick={() => setShowSwitchOperator(true)} title="Troca de turno — passar o caixa para outro operador sem deslogar" />
              <CommandTile testid="btn-close-shift" icon={Lock} label="Fechar" color="red" onClick={() => setShowCloseShift(true)} title="Encerrar seu turno informando os valores recebidos por forma de pagamento" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
